import React from "react";
import { MAX_METRICS, createMetric } from "../../utils/sport.js";

/**
 * Editor for the numeric values reported per set (or per activity).
 *
 * The count is implicit: adding a row adds a value, removing one takes it away
 * — which is friendlier on a phone than typing "how many" and then naming them.
 *
 * Anything can be a value, including reps — the rep count set on the station
 * is only a target to aim at, so logging what you actually managed means
 * adding "reps" here like any other number.
 *
 * @param {{id: string, name: string}[]} metrics
 * @param {(metrics: object[]) => void} onChange
 * @param {string} [hint] placeholder examples for the first field
 */
export default function MetricsEditor({
  metrics,
  onChange,
  hint = "e.g. kg, reps",
}) {
  const rename = (id, name) =>
    onChange(
      metrics.map((metric) =>
        metric.id === id ? { ...metric, name } : metric,
      ),
    );

  const remove = (id) => onChange(metrics.filter((metric) => metric.id !== id));

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[10px] uppercase tracking-widest text-ink-soft">
        values to report
      </p>

      {metrics.map((metric, index) => (
        <div key={metric.id} className="flex items-center gap-2">
          <span className="w-4 shrink-0 text-center font-hand text-base text-ink-soft">
            {index + 1}
          </span>

          <input
            value={metric.name}
            onChange={(event) => rename(metric.id, event.target.value)}
            placeholder={index === 0 ? hint : "name…"}
            maxLength={20}
            aria-label={`Value ${index + 1} name`}
            className="field-hand min-w-0 flex-1 font-hand text-lg leading-tight
                       placeholder:font-sans placeholder:text-xs placeholder:uppercase
                       placeholder:tracking-widest placeholder:text-ink-soft"
          />

          {/* At least one value must remain, otherwise there is nothing to log. */}
          {metrics.length > 1 && (
            <button
              type="button"
              onClick={() => remove(metric.id)}
              aria-label={`Remove value ${index + 1}`}
              className="shrink-0 rounded-pill px-1.5 text-xs text-ink-soft hover:text-accent"
            >
              ✕
            </button>
          )}
        </div>
      ))}

      {metrics.length < MAX_METRICS && (
        <button
          type="button"
          onClick={() => onChange([...metrics, createMetric()])}
          className="self-start pt-2.5 text-[10px] uppercase tracking-widest text-ink-soft
                     transition-colors hover:text-accent"
        >
          + value
        </button>
      )}
    </div>
  );
}
